import { Box, Button, Chip, Rating, Stack, StackProps, Tooltip, Typography } from "@mui/material"
import { PlusIcon, EditIcon } from "shared/icons"
import { ArticleResult } from "interface/article"
import { EditInterestDialog } from "features/kojin/dialog/editInterest"
import { useInterest } from "./container"
import { useUserSession } from "features/session/context"

type InterestListBlockProps = {
  articles: ArticleResult[] | null
  onArticleOpen: (article: ArticleResult, list: ArticleResult[]) => void
} & ReturnType<typeof useInterest> & StackProps
export const InterestListBlock = ({
	searchId,
	authorId,
  interestList,
  setInterestList,
  editInterest,
  setEditInterest,
  articles,
  onArticleOpen
}: InterestListBlockProps) => {
  const { isCareNet, isGeneral } = useUserSession()
  if (!searchId || !authorId) return <></>
  return (
    <>
      <Stack direction="column">
        {interestList.length > 0 ? (
          <Stack direction='column'>
            {interestList.map((interest, i) => (
              <Stack key={`interest-${interest.interestId}-${i}`} direction='row' padding={1} spacing={1} alignItems='flex-start'>
                <Stack flexGrow={1} direction='column' spacing={0.5}>
                  <Stack direction='row' spacing={1} paddingX={1} borderBottom={1} borderColor="divider">
                    <Typography variant="body2" color='text.primary' component='div' flexGrow={1}>{interest.title}</Typography>
                    { interest.ownFlag && <Chip label='自社製品' size='small' color='success' /> }
                    { interest.othersFlag && <Chip label='競合製品' size='small' color='warning' /> }
                    { interest.priorityContentFlag && <Chip label='承認外' size='small' color='error' /> }
                    { interest.articleScore && (
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Rating readOnly value={(interest.articleScore * 5) / 17} max={5} precision={0.1} size='small'/>
                        <Typography variant='caption' color='text.secondary'>{interest.articleScore}/17 点</Typography>
                      </Stack>
                    ) }
                  </Stack>
                  <Stack direction='column' spacing={0.5} paddingX={1}>
                    { interest.reason && <Typography variant="body2" color='text.secondary' whiteSpace='pre-wrap'>{interest.reason}</Typography> }
                    { interest.ref && interest.ref.length > 0 && (
                      <Stack direction='row' flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" color='text.secondary' component='div' marginRight={1}>参照論文</Typography>
                        { interest.ref.map((ref, k) => {
                          if (!articles) return <></>
                          const index = articles.findIndex(article => article.articleId === ref.articleId) ?? -1
                          if (index < 0) return <></>
                          const article = articles[index]
                          if (!article) return <></>
                          return (
                            <Tooltip key={`interest-ref-${i}-${k}`} title={ref.linkText} placement='top'>
                              <Chip label={`${index+1}`} variant='outlined' size='small' sx={{ marginRight: 0.5, borderRadius: 1, height: 'inherit' }} onClick={() => {
                                const list = articles?.filter((journal) => interest.ref.some((r) => r.articleId === journal.articleId)) ?? []
                                onArticleOpen(article, list)
                              }}/>
                            </Tooltip>
                          )
                        }) }
                      </Stack>
                    ) }
                  </Stack>
                </Stack>
                {isCareNet && isGeneral && (
                  <Button
                    variant="text"
                    size='small'
                    startIcon={<EditIcon/>}
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => setEditInterest({ action: 'edit', data: interest })}
                  >
                    編集
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        ) : <></>}

        {isCareNet && isGeneral && (
          <Box paddingX={1}>
            <Button
              variant="text"
              size='small'
              startIcon={<PlusIcon/>}
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => setEditInterest({ action: 'create', data: null })}
            >
              項目を追加
            </Button>
          </Box>
        )}
      </Stack>

      {(editInterest && isCareNet && isGeneral) && (
        <EditInterestDialog
          searchId={searchId}
          authorId={authorId}
          data={editInterest.data}
          articles={articles ?? []}
          onDelete={() => {
            if (!editInterest || editInterest.action !== 'edit' || !editInterest.data) return
            const removeId = editInterest.data.interestId
            setInterestList(interestList.filter((interest) => interest.interestId !== removeId))
            setEditInterest(null)
          }}
          onSave={(data) => {
            if (!editInterest) return
            if (editInterest.action === 'edit') {
              if (!editInterest.data) return
              const editId = editInterest.data.interestId
              setInterestList(interestList.map((interest) => interest.interestId === editId ? data : interest))
            } else if (editInterest.action === 'create') {
              setInterestList(interestList.concat([data]))
            }
            setEditInterest(null)
          }}
          onClose={() => setEditInterest(null)}
        />
      )}
    </>
  )
}